/**
 * Shared GTFS stop id normalization helpers used across map and services layers.
 */

/**
 * Strips the optional `Parent` prefix from GTFS parent station ids.
 *
 * @param parentStation Raw GTFS `parent_station` value.
 * @returns Normalized parent suffix used for child matching.
 */
export const normalizeGtfsParentStationId = ( parentStation: string ): string => parentStation.trim().replace( /^Parent/, "" );

/**
 * Returns the unprefixed GTFS stop id suffix (`{gtfs_source}_` removed when known).
 *
 * @param stopId Prefixed stop id from the spatial index.
 * @param gtfsSource GTFS source prefix when available.
 * @returns Stop id suffix without the source prefix.
 */
export const getGtfsStopIdSuffix = ( stopId: string, gtfsSource: string | null ): string => {
    const sourcePrefix = gtfsSource !== null && gtfsSource !== "" ? `${ gtfsSource }_` : "";

    if ( sourcePrefix !== "" && stopId.startsWith( sourcePrefix ) ) {
        return stopId.slice( sourcePrefix.length );
    }

    const underscoreIndex = stopId.indexOf( "_" );

    return underscoreIndex >= 0 ? stopId.slice( underscoreIndex + 1 ) : stopId;
};

/** Swiss URN pattern — `ch:1:sloid:{uic}` (optionally followed by platform segments). */
const SLOID_UIC_PATTERN = /sloid:(\d+)/;

/** Navitia/SNCF stop families carrying the UIC after `OCE` or the last `-`. */
const NAVITIA_STOP_PREFIXES = [ "StopPoint:", "StopArea:" ] as const;

/** `OCE{uic}` remainder once `StopPoint:`/`StopArea:` has been stripped. */
const OCE_UIC_PATTERN = /^OCE(\d+)$/;

/**
 * Platform suffix emitted by Swiss-style feeds (`8592663:0:N`, `8501238:0:1`) and
 * similar `:<0|1>:<platform>` tails. Deliberately narrow: generic `X:rest` ids
 * (`FR:…`, `0:1ABE`, `ZOS:…`) must keep their full suffix to stay unique per stop.
 */
const PLATFORM_SUFFIX_PATTERN = /:[01]:[^:]{1,12}$/;

/**
 * Extracts the station identifier from the Navitia remainder of a
 * `StopPoint:`/`StopArea:` id (`OCETrain TER-87723197` → `87723197`,
 * `OCE71043075` → `71043075`). Returns the remainder unchanged when no UIC is found.
 *
 * @param rest Id text after the `StopPoint:`/`StopArea:` prefix.
 * @returns UIC suffix when derivable, otherwise the input remainder.
 */
const resolveNavitiaRemainder = ( rest: string ): string => {
    const dashIndex = rest.lastIndexOf( "-" );

    if ( dashIndex >= 0 && dashIndex < rest.length - 1 ) {
        return rest.slice( dashIndex + 1 );
    }

    const oceMatch = OCE_UIC_PATTERN.exec( rest );

    if ( oceMatch !== null ) {
        return oceMatch[1];
    }

    return rest;
};

/**
 * Derives the `/api/stops/lines` station key for one (prefixed) GTFS stop id.
 * Mirror of the backend `resolveGtfsStationKey` — keep both aligned.
 *
 * Ordered rules — earlier matches win:
 * 1. strip the exact `{gtfs_source}_` prefix when present;
 * 2. strip a leading `Parent` marker (`Parentch:…`, `Parent8508128`);
 * 3. `sloid:{uic}` → Swiss UIC (`ch:1:sloid:X`, platforms `…:0:Y` included);
 * 4. `StopPoint:`/`StopArea:` → Navitia UIC (after last `-`, else after `OCE`);
 * 5. bare `OCE…` ids → same Navitia handling;
 * 6. `:<0|1>:<platform>` suffix → station part only;
 * 7. otherwise → the full unprefixed id (unique per stop: `FR:…`, `0:1ABE`,
 *    `stop_area:SA_1`, `zenbus:…`, `RTM-…`, `place_WSTIS1`…).
 *
 * @param stopId Prefixed `stop_id` (e.g. `tpg_ch:1:sloid:10154:0:392238`) or raw suffix.
 * @param gtfsSource GTFS source key (e.g. `tpg`); empty/null skips exact-prefix stripping.
 * @returns Station key grouping platforms of one station ("" when input is blank).
 */
export const resolveGtfsStationKey = ( stopId: string, gtfsSource: string | null | undefined ): string => {
    const trimmed = stopId.trim();

    if ( trimmed === "" ) {
        return "";
    }

    const sourcePrefix = typeof gtfsSource === "string" && gtfsSource.trim() !== ""
        ? `${ gtfsSource.trim() }_`
        : "";
    const suffix = sourcePrefix !== "" && trimmed.startsWith( sourcePrefix )
        ? trimmed.slice( sourcePrefix.length )
        : trimmed;
    const base = suffix.replace( /^Parent/, "" );

    const sloidMatch = SLOID_UIC_PATTERN.exec( base );

    if ( sloidMatch !== null ) {
        return sloidMatch[1];
    }

    for ( const prefix of NAVITIA_STOP_PREFIXES ) {
        if ( base.startsWith( prefix ) ) {
            return resolveNavitiaRemainder( base.slice( prefix.length ) );
        }
    }

    if ( base.startsWith( "OCE" ) ) {
        const resolved = resolveNavitiaRemainder( base );

        if ( resolved !== base ) {
            return resolved;
        }
    }

    if ( PLATFORM_SUFFIX_PATTERN.test( base ) ) {
        return base.replace( PLATFORM_SUFFIX_PATTERN, "" );
    }

    return base;
};
